import {
  createCalendar,
  fromDate,
  toCalendarDate,
  today,
  type CalendarDate,
} from "@internationalized/date";
import { useLocale, useTranslations } from "next-intl";
import { useMemo, useReducer } from "react";
import { useCalendarState } from "react-stately";

import type { StartTime } from "@trip/common";
import { TIMEZONE } from "@trip/constants";

import { type StartTimeFieldValue } from "~/components/start-times";
import { trpcReact } from "~/trpc/client";

import {
  getPassengerCategories,
  validate,
  type ProductId,
  type TourConfig,
} from "./tours";
import { useCart, type TourCartLine } from "./use-cart";
import { useTrack } from "./use-track";

function getDefaultValue(cartLine?: TourCartLine, inventoryDate?: Date) {
  if (cartLine?.lineItem.date) {
    return toCalendarDate(fromDate(cartLine.lineItem.date, "UTC"));
  }
  if (inventoryDate) {
    return toCalendarDate(fromDate(inventoryDate, "UTC"));
  }
  return today(TIMEZONE).add({ days: 1 });
}

interface State {
  date: CalendarDate | null;
  passengerCategories: { id: string; count: number }[] | null;
  planId: string;
  startTime: { hour: number; minute: number } | null;
}

type Action =
  | {
      type: "update-passenger-count";
      id: string;
      count: number;
      config: TourConfig;
    }
  | { type: "update-date"; date: CalendarDate }
  | { type: "update-start-time"; starTime: { hour: number; minute: number } }
  | { type: "update-plan"; id: string };

function getDefaultPassengerCategories(
  passengerCategories: TourConfig["passengerCategories"],
) {
  const pCats = passengerCategories
    .map((passengerCategory) =>
      passengerCategory.isDefault
        ? { id: passengerCategory.id, count: 2 }
        : null,
    )
    .filter((x): x is { id: string; count: number } => x !== null);

  if (pCats.length === 0 && passengerCategories[0]) {
    return [{ id: passengerCategories[0].id, count: 1 }];
  }
  return pCats;
}

export function useTourCalendar({
  datoTourId,
  productId,
  onChange,
  config: serverConfig,
  inventoryDate,
}: {
  datoTourId: string;
  productId: ProductId;
  onChange?: () => void;
  config: TourConfig;
  inventoryDate?: Date;
}) {
  const track = useTrack();
  const t = useTranslations("TourDetails");

  const cartLine = useCart()
    .cart?.cartLines.filter(
      (cartLine) => cartLine.type === "bokun" || cartLine.type === "linktivity",
    )
    .find((cartLine) => {
      return cartLine.lineItem.datoTourId === datoTourId;
    });

  const [state, dispatch] = useReducer(
    (state: State, action: Action): State => {
      switch (action.type) {
        case "update-passenger-count":
          return {
            ...state,
            passengerCategories: action.config.passengerCategories
              .map(({ id }) => {
                const count =
                  state.passengerCategories?.find(
                    (passengerCategory) => passengerCategory.id === id,
                  )?.count ?? 0;
                return id === action.id
                  ? { id, count: action.count }
                  : { id, count };
              })
              .filter(({ count }) => count > 0),
          };
        case "update-date":
          return { ...state, date: action.date };
        case "update-plan":
          return { ...state, planId: action.id };
        case "update-start-time":
          return { ...state, startTime: action.starTime };
        default:
          // oxlint-disable-next-line no-unused-expressions
          action satisfies never;
          return state;
      }
    },
    {
      date: getDefaultValue(cartLine, inventoryDate),
      passengerCategories: getDefaultPassengerCategories(
        serverConfig.passengerCategories,
      ),
      planId: serverConfig.plan.id,
      startTime: null,
    } satisfies State,
  );

  const locale = useLocale();

  const calendarState = useCalendarState<CalendarDate>({
    locale,
    createCalendar,
    defaultValue: state.date,
    minValue: today(TIMEZONE),
    onChange: (value) => {
      dispatch({ type: "update-date", date: value });
      track({ name: "Change Date", date: value.toString() });
      onChange?.();
    },
  });

  const configQuery = trpcReact.tours.config.useQuery(
    {
      productId,
      planId: state.planId,
      date: calendarState.value?.toDate("UTC"),
    },
    { initialData: serverConfig, refetchOnWindowFocus: false },
  );

  const config = configQuery.data;
  const plan =
    serverConfig.plans.find((plan) => plan.id === state.planId) ??
    configQuery.data.plan;

  const choicesQuery = trpcReact.tours.choices.useQuery(
    {
      productId,
      start: calendarState.visibleRange.start.toDate("UTC"),
      end: calendarState.visibleRange.end.toDate("UTC"),
      passengerCategories: state.passengerCategories ?? [],
      planId: state.planId,
    },
    { enabled: !plan.openDate },
  );

  const passengerCategories = getPassengerCategories(
    state.passengerCategories ?? [],
    cartLine,
    config,
  );

  const choices = choicesQuery.data ?? [];
  let choice:
    | {
        date?: Date;
        startTimes: StartTime[];
        amount: number;
      }
    | undefined = choices.find(
    (choice) => state.date?.compare(fromDate(choice.date, "UTC")) === 0,
  );

  if (!choice && plan.openDate && plan.openDateAmounts) {
    const totalAmount = plan.openDateAmounts.reduce(
      (agg, { passengerCategory, totalAmount }) => {
        const count =
          passengerCategories.find(({ id }) => id === passengerCategory.id)
            ?.count ?? 0;
        return agg + count * totalAmount;
      },
      0,
    );
    choice = { amount: totalAmount, startTimes: [] };
  }

  const choiceStartTimes = choice?.startTimes ?? [];
  const stateStartTime = state.startTime;
  const startTime = stateStartTime
    ? choiceStartTimes.find(
        (startTime) =>
          startTime.hour === stateStartTime.hour &&
          startTime.minute === stateStartTime.minute,
      )
    : (choiceStartTimes[0] ?? null);

  const totalPassengers = passengerCategories.reduce<number>(
    (agg, { count }) => agg + count,
    0,
  );

  const startTimes: StartTimeFieldValue[] = useMemo(() => {
    return (
      (choice ? [...choice.startTimes] : null) ??
      config.startTimes.map((startTime) => ({
        ...startTime,
        disabled: true,
      }))
    );
    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calendarState.value, choicesQuery.isFetching || configQuery.isFetching]);

  return {
    choice,
    choices,
    startTime,
    startTimes,
    date: state.date,
    passengerCategories,
    plan,
    isFetching: choicesQuery.isFetching || configQuery.isFetching,
    dispatch,
    calendarState,
    cartLine,
    totalPassengers,
    config,
    errors: validate({
      t,
      totalPassengers,
      maxPassengersPerBooking: config.maxPassengersPerBooking,
      minPassengersPerBooking: config.minPassengersPerBooking,
      totalQuantityAvailable: startTime?.totalQuantityAvailable,
    }),
  };
}

export type TourCalendar = ReturnType<typeof useTourCalendar>;
